import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Trophy, Copy, Check, Crown, Plus, Info, Users, UserPlus, MessageSquare, Table } from 'lucide-react';
import LeagueTable from './LeagueTable';
import LeagueMembers from './LeagueMembers';
import LeagueNotices from './LeagueNotices';
import LeagueVacancy from './LeagueVacancy';
import LeagueCancel from './LeagueCancel';
import LeagueJoinRequests from './LeagueJoinRequests';
import ForkLeagueModal from './ForkLeagueModal';
import InvitePeopleModal from '../invitations/InvitePeopleModal';
import ShareLeague from './ShareLeague';
import AddTournamentModal from './AddTournamentModal';
import Banterboard from '../banter/Banterboard';
import PickCallout from '../picks/PickCallout';
import GafferToggle from '../../components/GafferToggle';
import { getStatusConfig, FINISHED_STATUSES } from '../games/gameStatus';
import * as miniLeaguesApi from '../../api/miniLeagues';
import * as tournamentsApi from '../../api/tournaments';
import * as notificationsApi from '../../api/notifications';

// A running game opens on its pick tab, a finished one on Game Progress —
// there is nothing to pick once the game is over, and the progress board is
// the result. Cancelled counts as finished for this purpose.
const isFinished = (gameStatus) => FINISHED_STATUSES.includes(gameStatus);

const gameLink = (game) => (isFinished(game.gameStatus)
  ? { tab: 'progress', label: 'View Progress' }
  : { tab: 'picks', label: game.current ? 'Make Your Pick' : 'Open Game' });

/**
 * Every cycle of a tournament, newest first. Falls back to the tournament's
 * current-game pointer when the league payload predates the games list, so an
 * older response still gets a link into the live game.
 */
const gamesFor = (tournament) => {
  if (tournament.games && tournament.games.length > 0) return tournament.games;
  if (!tournament.currentGameId) return [];
  return [{
    gameId: tournament.currentGameId,
    gameStatus: tournament.currentGameStatus,
    gameName: null,
    current: true,
  }];
};

function LeagueDetailsPage({ leagueId, currentUser, onBack, onViewGame }) {
  const [league, setLeague] = useState(null);
  const [entries, setEntries] = useState([]);
  const [members, setMembers] = useState([]);
  const [notices, setNotices] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [picksNeeded, setPicksNeeded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAddTournament, setShowAddTournament] = useState(false);
  const [showFork, setShowFork] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [savingGaffer, setSavingGaffer] = useState(false);
  const [gafferError, setGafferError] = useState('');
  const [activeTab, setActiveTab] = useState('league');

  // `silent` refreshes data without flipping the page into its loading state.
  // A full reload would unmount the members list and throw away the outcome
  // message it just showed (notably "leaving after this game").
  const loadAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const leagueData = await miniLeaguesApi.getLeague(leagueId);
      setLeague(leagueData);
      // Best-effort extras: an empty table or no notices is a normal state for a
      // new league and must not block the page.
      const [leaderboard, memberList, noticeList, requestList, pending] = await Promise.all([
        miniLeaguesApi.getLeaderboard(leagueId).catch(() => []),
        miniLeaguesApi.getMembers(leagueId).catch(() => []),
        miniLeaguesApi.getNotices(leagueId).catch(() => []),
        // Manager-only endpoint; a plain member gets a 400 and simply has none.
        miniLeaguesApi.getJoinRequests(leagueId).catch(() => []),
        // The account-wide list, narrowed to this league below. One read rather than
        // one per game, and the open-week and already-picked rules stay on the server.
        notificationsApi.getPendingActions().catch(() => []),
      ]);
      setEntries(leaderboard || []);
      setMembers(memberList || []);
      setNotices(noticeList || []);
      setJoinRequests(requestList || []);
      setPicksNeeded((pending || []).filter(
        (a) => a.type === 'PICK_NEEDED' && String(a.leagueId) === String(leagueId)
      ));
    } catch (err) {
      console.error('Error loading league:', err);
      setError('Failed to load league');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    tournamentsApi.getTournaments()
      .then(setTournaments)
      .catch((err) => console.error('Error loading tournaments:', err));
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(league.leagueCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /**
   * Applies to games this league creates from here on, so a running game is untouched.
   *
   * Throws on failure rather than swallowing, so GafferToggle can put its own box back —
   * the tick is applied the moment it is pressed and this is the only signal that the
   * save did not stick.
   */
  const handleGafferChange = async (enabled) => {
    setGafferError('');
    setSavingGaffer(true);
    try {
      await miniLeaguesApi.setGafferEnabled(leagueId, enabled);
      await loadAll({ silent: true });
    } catch (err) {
      setGafferError(err.body?.error || 'Could not update the Gaffer setting');
      throw err;
    } finally {
      setSavingGaffer(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container fade-in">
        <div className="content-card">
          <div className="loading-spinner">Loading league...</div>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="page-container fade-in">
        <div className="content-card">
          <button className="secondary-btn" onClick={onBack}>
            <ArrowLeft size={18} />
            Back
          </button>
          <div className="empty-state">
            <h3>League Not Found</h3>
            <p>{error || 'This league could not be loaded.'}</p>
          </div>
        </div>
      </div>
    );
  }

  // A vacant league has no managerId, so nobody is manager until someone claims it.
  const isManager = !league.vacant && String(league.managerId) === String(currentUser.id);
  const isMember = members.some((m) => String(m.playerId) === String(currentUser.id));
  const tournamentList = league.tournaments || [];
  const unacknowledged = notices.filter((n) => !n.acknowledged);

  /**
   * Cycles still running whose Gaffer state disagrees with the league setting.
   *
   * The setting is a default for cycles the league has yet to create, which is easy to
   * write and easy to misread: a manager who ticks the box mid-season sees a ticked box
   * and a game the Gaffer is not in. Naming those games is the difference between an
   * honest setting and one that looks broken.
   */
  const gafferMismatches = tournamentList
    .flatMap((t) => gamesFor(t).map((g) => ({ ...g, tournamentName: t.tournamentName })))
    .filter((g) => g.current && !isFinished(g.gameStatus)
      && Boolean(g.gafferEnabled) !== Boolean(league.gafferEnabled));

  const gafferNote = gafferMismatches.length === 0
    ? 'Applies to games this league starts from here on.'
    : league.gafferEnabled
      ? `Applies to games this league starts from here on — he is not in the ${gafferMismatches.map((g) => g.tournamentName).join(' or ')} game already running. Add him from that game's page while it is still open.`
      : `Applies to games this league starts from here on — he is still in the ${gafferMismatches.map((g) => g.tournamentName).join(' and ')} game already running. Withdraw him from that game's page.`;

  const banterTab = () => (
    <>
      {tournamentList.length === 0 ? (
        <div className="empty-state">
          <MessageSquare size={56} />
          <h3>The Boot Room Isn't Open Yet</h3>
          <p>
            {isManager
              ? 'Add a tournament and the Boot Room opens with it'
              : 'The Boot Room opens when the league manager adds a tournament'}
          </p>
        </div>
      ) : (
        tournamentList.map((t) => (
          <Banterboard
            key={t.id}
            tournamentId={t.id}
            tournamentName={t.tournamentName}
            currentUserId={currentUser?.id}
            // Only worth labelling when there is more than one board to tell apart.
            showHeading={tournamentList.length > 1}
            // A league code always does something: straight in, or a request (item 90).
            share={{ name: league.name, code: league.leagueCode }}
          />
        ))
      )}
    </>
  );

  return (
    <div className="page-container fade-in">
      <div className="content-card">
        <button className="secondary-btn league-back" onClick={onBack}>
          <ArrowLeft size={18} />
          Back to My Leagues
        </button>

        <div className="card-header">
          <Trophy className="header-icon" size={48} />
          <h1>{league.name}</h1>
          <p>{isManager ? 'You manage this league' : 'Season league table'}</p>
        </div>

        {/*
          The league is over when the last of its tournaments finished - a season bounds which ones
          it can hold, so there is a genuine last one. "No winner" is a real outcome for a league
          where nobody scored, so it is stated rather than left blank.
        */}
        {league.complete && (
          <div className="league-winner">
            {league.winners && league.winners.length > 0 ? (
              <>🏆 League winner{league.winners.length > 1 ? 's' : ''}:{' '}
                <strong>{league.winners.join(' & ')}</strong>
              </>
            ) : (
              <>League finished — no winner</>
            )}
          </div>
        )}

        {/*
          A cancelled league did not finish, so it says something different: there is no winner
          to go and look at, there is a reason. Whoever called it off is named because "your
          manager ended this" and "we ended this" are not the same message.
        */}
        {league.cancelled && (
          <div className="league-notice league-cancelled">
            <strong>This league was cancelled</strong>
            <span>
              {league.cancelledBy === 'ADMIN' ? 'Cancelled by an administrator' : 'Cancelled by the manager'}
              {league.cancellationReason ? ` — “${league.cancellationReason}”` : ''}
            </span>
          </div>
        )}

        {/* Above everything, including the tabs. A pick has a deadline and costs the
            player the game if they miss it; nothing else on this page does either. */}
        <PickCallout
          actions={picksNeeded}
          onOpenGame={onViewGame}
          leagueName={league.name}
        />

        <LeagueVacancy
          league={league}
          isManager={isManager}
          isMember={isMember}
          onChanged={() => loadAll({ silent: true })}
        />

        {/* Two tabs, because the board grew into something people came to the page for
            rather than a footnote under the fixtures. It also has to be reachable in one
            tap from the top - buried at the bottom of the tournaments list, most members
            never found out it was there. */}
        <div className="league-tabs" role="tablist" aria-label="League sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'league'}
            className={`league-tab${activeTab === 'league' ? ' on' : ''}`}
            onClick={() => setActiveTab('league')}
          >
            <Table size={17} />
            League
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'banter'}
            className={`league-tab banter${activeTab === 'banter' ? ' on' : ''}`}
            onClick={() => setActiveTab('banter')}
          >
            <MessageSquare size={17} />
            Boot Room
          </button>
        </div>

        {activeTab === 'banter' ? banterTab() : (
          <>
            {/* Sharing is open to every member, not just the manager - getting mates in
                is everyone's job, and the manager can remove anyone they don't want. */}
            <div className="game-code-display league-code-inline">
              <span className="code-label">League Code:</span>
              <span className="code-value">{league.leagueCode}</span>
              <button className="copy-btn" onClick={copyCode} title="Copy league code">
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <ShareLeague league={league} />
            </div>

            {!league.joinOpen && (
              <p className="helper-text">
                The season has started, so anyone you share this with will need the
                manager to wave them through.
              </p>
            )}

            {unacknowledged.length > 0 && (
              <div className="league-notice">
                <Info size={18} />
                <span>
                  {unacknowledged.length === 1
                    ? 'A new game has started in this league.'
                    : `${unacknowledged.length} new games have started in this league.`}
                </span>
              </div>
            )}

            {isManager && (
              <LeagueJoinRequests
                requests={joinRequests}
                leagueId={league.id}
                onChanged={() => loadAll({ silent: true })}
              />
            )}

            <h2 className="league-section-title">League Table</h2>
            <LeagueTable entries={entries} currentUser={currentUser} managerId={league.managerId} />

            <h2 className="league-section-title">
              Members{members.length > 0 ? ` (${members.length})` : ''}
            </h2>
            <LeagueMembers
              members={members}
              currentUser={currentUser}
              isManager={isManager}
              leagueId={league.id}
              onChanged={() => loadAll({ silent: true })}
            />

            <div className="league-section-header">
              <h2 className="league-section-title">Tournaments</h2>
              {isManager && (
                <button className="primary-btn" onClick={() => setShowAddTournament(true)}>
                  <Plus size={18} />
                  Add Tournament
                </button>
              )}
            </div>

            {tournamentList.length === 0 ? (
              <div className="empty-state">
                <Trophy size={56} />
                <h3>No Tournaments Yet</h3>
                <p>
                  {isManager
                    ? 'Add a tournament to start the first game of the season'
                    : 'The league manager has not added a tournament yet'}
                </p>
              </div>
            ) : (
              <div className="league-tournaments">
                {tournamentList.map((t) => {
                  const status = getStatusConfig({ gameStatus: t.currentGameStatus });
                  const games = gamesFor(t);
                  return (
                    <div key={t.id} className="league-tournament-row detailed">
                      <div className="league-tournament-main">
                        <span className="league-tournament-name">{t.tournamentName}</span>
                        {t.tournamentActive ? (
                          <span
                            className="status-badge"
                            style={{ backgroundColor: status.color, color: status.textColor }}
                          >
                            {status.label}
                          </span>
                        ) : (
                          <span className="status-badge" style={{ backgroundColor: '#616161', color: '#fff' }}>
                            Finished
                          </span>
                        )}
                      </div>

                      {/* Each cycle gets its own row, so a finished game stays reachable
                          instead of disappearing when the next one is auto-created. */}
                      {games.length === 0 ? (
                        <p className="helper-text">No game has started for this tournament yet.</p>
                      ) : (
                        <div className="league-game-list">
                          {games.map((g) => {
                            const link = gameLink(g);
                            const gameStatus = getStatusConfig({ gameStatus: g.gameStatus });
                            // The pick button is the loud one wherever it appears - the
                            // callout at the top of the page and this row are the same ask.
                            const needsPick = picksNeeded.some(
                              (a) => String(a.gameId) === String(g.gameId)
                            );
                            return (
                              <div
                                key={g.gameId}
                                className={`league-game-row${g.current ? ' current' : ''}`}
                              >
                                <div className="league-game-info">
                                  <span className="league-game-name">
                                    {g.gameName || `${t.tournamentName} game`}
                                  </span>
                                  <span
                                    className="status-badge"
                                    style={{ backgroundColor: gameStatus.color, color: gameStatus.textColor }}
                                  >
                                    {gameStatus.label}
                                  </span>
                                  {g.gameWeek > 0 && (
                                    <span className="league-game-week">Week {g.gameWeek}</span>
                                  )}
                                </div>
                                <button
                                  className={`view-game-btn league-game-btn${g.current ? '' : ' past'}${needsPick ? ' pick-needed' : ''}`}
                                  onClick={() => onViewGame(g.gameId, link.tab)}
                                >
                                  {needsPick ? 'Make your pick' : link.label}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <h2 className="league-section-title">League Activity</h2>
            <LeagueNotices
              notices={notices}
              tournaments={tournamentList}
              isManager={isManager}
              leagueId={league.id}
              onChanged={() => loadAll({ silent: true })}
              onViewGame={onViewGame}
            />

            {isManager && (
              <p className="helper-text">
                <Crown size={13} /> As manager you can add tournaments. Members join
                once and are entered into every tournament, including ones you add later.
              </p>
            )}

            {isManager && (
              <GafferToggle
                checked={Boolean(league.gafferEnabled)}
                onChange={handleGafferChange}
                disabled={savingGaffer}
                disabledReason={savingGaffer ? 'Saving...' : ''}
                note={gafferNote}
              />
            )}
            {gafferError && <p className="error-text">{gafferError}</p>}

            {/* Any member can fork, not just the manager - it is how new leagues start. */}
            {isMember && (
              <div className="league-member-actions">
                <button className="secondary-btn" onClick={() => setShowFork(true)}>
                  <Users size={16} />
                  Start a new league with these players
                </button>
                {isManager && (
                  <button className="secondary-btn" onClick={() => setShowInvite(true)}>
                    <UserPlus size={16} />
                    Invite people
                  </button>
                )}
              </div>
            )}

            <LeagueVacancy
              mode="actions"
              league={league}
              isManager={isManager}
              isMember={isMember}
              onChanged={() => loadAll({ silent: true })}
            />

            <LeagueCancel
              league={league}
              isManager={isManager}
              onChanged={() => loadAll({ silent: true })}
            />
          </>
        )}
      </div>

      {showInvite && (
        <InvitePeopleModal
          scope="MINI_LEAGUE"
          scopeId={league.id}
          title={`Invite people to ${league.name}`}
          onClose={() => setShowInvite(false)}
          onInvited={() => loadAll({ silent: true })}
        />
      )}

      {showFork && (
        <ForkLeagueModal
          league={league}
          currentUser={currentUser}
          onClose={() => setShowFork(false)}
          onForked={() => {}}
        />
      )}

      {showAddTournament && (
        <AddTournamentModal
          league={league}
          tournaments={tournaments}
          onClose={() => setShowAddTournament(false)}
          onAdded={() => {
            setShowAddTournament(false);
            loadAll();
          }}
        />
      )}
    </div>
  );
}

export default LeagueDetailsPage;
